import { useState, useCallback, useEffect } from "react";
import { Modal, Button, Input, Dropdown, Form } from "semantic-ui-react";

// Serializer
import ProductServiceSerializer from "issara-sdk/types/ProductServiceSerializer_core";

// Component
import DateTextBox from "react-lib/apps/common/DateTextBox";
import {
  BTN_ACTS,
  PickedState,
  SetProp,
  serviceStatusOption,
} from "react-lib/apps/MEH/sequence/SaveServiceOrder";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

type ServiceFormData = {
  service_code_prefix: string;
  service_code: string;
  name_th: string;
  name_en: string;
  product_type: number | null;
  price: string;
  unit: number | null;
  description: string;
  start_date: string;
  end_date: string;
  status: number | null;
};

type ModSettingRuleProps = {
  setProp: SetProp;
  open: boolean;
  onClose: () => void;
  // Options
  productTypeOptions?: { key: number; value: number; text: string }[];
  unitOptions?: { key: number; value: number; text: string }[];
  statusOptions?: { key: number; value: number; text: string }[];
  selectService?: Partial<ProductServiceSerializer>;
  // Callbacks
  onSave?: (data: ProductServiceSerializer) => void;
} & Pick<
  PickedState,
  | "loadingStatus"
  | "buttonLoadCheck"
  | "errorMessage"
  | "successMessage"
  | "selectedDivision"
  | "userTokenize"
  | "masterOptions"
>;

const initialForm: Partial<ProductServiceSerializer> = {
  code: "",
  name: "",
  name_en: "",
  price: "",
  unit: null,
  description: "",
  start_date: "",
  end_date: "",
  active_flag: null,
};

const ModSettingRule = (props: ModSettingRuleProps) => {
  const [formData, setFormData] = useState<Partial<ProductServiceSerializer>>({});

  useEffect(() => {
    if (props.open) {
      if (props.selectService?.code) {
        setFormData(props.selectService);
      } else {
        setFormData({
          ...initialForm,
          of_division: props.selectService?.of_division,
        });
      }
    }
  }, [props.selectService, props.open]);

  const handleChangeInput = useCallback((key: keyof ProductServiceSerializer, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleChangeDate = useCallback(
    (name: keyof ProductServiceSerializer) => (date: string) => {
      handleChangeInput(name, date);
    },
    [],
  );

  const handleClose = () => {
    setFormData({});
    props.onClose();
  };

  const handleSave = () => {
    props.onSave?.(formData);
  };

  return (
    <div className="ui form">
      <Modal
        key={props.selectService?.code || "new"}
        open={props.open}
        onClose={handleClose}
        size="small"
        closeOnDimmerClick={false}
      >
        <Modal.Header style={{ borderBottom: "none", padding: "20px 24px 10px" }}>
          <span style={{ fontSize: "18px", fontWeight: "bold" }}>{`${
            !props.selectService?.code ? "สร้างรายการบริการใหม่" : "แก้ไขรายการบริการ"
          }`}</span>
          <div style={{ borderTop: "solid 2px var(--primary-theme-color)", marginTop: "4px" }} />
        </Modal.Header>

        <Modal.Content style={{ padding: "10px 24px 20px" }}>
          <Form className="--override-disabled --grey">
            {/* รหัสบริการ */}
            <Form.Field required style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>รหัสบริการ</label>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Input disabled={true} value={formData?.of_division} style={{ width: "70px" }} />
                <span>-</span>
                <Input
                  placeholder="ระบุรหัสบริการ"
                  value={formData?.code}
                  onChange={(e, { value }) => handleChangeInput("code", value)}
                  style={{ flex: 1 }}
                />
              </div>
            </Form.Field>

            {/* ชื่อบริการ (TH) */}
            <Form.Field required style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>ชื่อบริการ (TH)</label>
              <Input
                placeholder="ชื่อบริการภาษาไทย"
                value={formData?.name}
                onChange={(e, { value }) => handleChangeInput("name", value)}
                fluid
              />
            </Form.Field>

            {/* ชื่อบริการ (ENG) */}
            <Form.Field style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>ชื่อบริการ (ENG)</label>
              <Input
                placeholder="ชื่อบริการภาษาอังกฤษ"
                value={formData?.name_en}
                onChange={(e, { value }) => handleChangeInput("name_en", value)}
                fluid
              />
            </Form.Field>

            {/* ประเภทบริการ */}
            <Form.Field required style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>ประเภทบริการ</label>
              <Dropdown
                placeholder="-- เลือกประเภทบริการ --"
                search
                selection
                fluid
                options={props.productTypeOptions || []}
                value={formData?.p_type || 0}
                onChange={(e, { value }) => handleChangeInput("p_type", value)}
              />
            </Form.Field>

            {/* ราคา */}
            <Form.Field required style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>ราคา</label>
              <Input
                placeholder="ระบุราคา"
                value={formData?.price ?? ""}
                onChange={(e, { value }) => {
                  if (/^\d*\.?\d*$/.test(value)) {
                    handleChangeInput("price", value);
                  }
                }}
                fluid
                inputMode="decimal"
              />
            </Form.Field>

            {/* หน่วยขาย */}
            <Form.Field required style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>หน่วยขาย</label>
              <Dropdown
                placeholder="-- เลือกหน่วยขาย --"
                search
                selection
                fluid
                options={props.unitOptions || []}
                value={formData?.unit || 0}
                onChange={(e, { value }) => handleChangeInput("unit", value)}
              />
            </Form.Field>

            {/* รายละเอียดเพิ่มเติม */}
            <Form.Field style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>รายละเอียดเพิ่มเติม</label>
              <Input
                placeholder="รายละเอียดเพิ่มเติม"
                value={formData?.description}
                onChange={(e, { value }) => handleChangeInput("description", value)}
                fluid
              />
            </Form.Field>

            {/* วันเวลาเริ่มต้นการขาย (ราคานี้) */}
            <Form.Field required style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>วันเวลาเริ่มต้นการขาย (ราคานี้)</label>
              <DateTextBox
                placeholder="DD/MM/YYYY"
                value={formData?.start_date}
                onChange={handleChangeDate("start_date")}
              />
            </Form.Field>

            {/* วันเวลาสิ้นสุดการขาย (ราคานี้) */}
            <Form.Field required style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>วันเวลาสิ้นสุดการขาย (ราคานี้)</label>
              <DateTextBox
                placeholder="DD/MM/YYYY"
                value={formData?.end_date}
                onChange={handleChangeDate("end_date")}
              />
            </Form.Field>

            {/* สถานะการขาย */}
            <Form.Field required style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>สถานะการขาย</label>
              <Dropdown
                search
                selection
                fluid
                options={serviceStatusOption}
                value={formData?.active_flag || 0}
                onChange={(e, { value }) => handleChangeInput("active_flag", value)}
              />
            </Form.Field>
          </Form>
        </Modal.Content>

        <Modal.Actions style={{ borderTop: "none", padding: "10px 24px 20px", textAlign: "right" }}>
          <Button
            onClick={handleClose}
            style={{
              backgroundColor: "#5C5C5C",
              color: "white",
              minWidth: "120px",
            }}
            size="small"
          >
            ปิดหน้าจอ
          </Button>
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleSave}
            // data
            paramKey={BTN_ACTS.CREATE_SERVICE}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.CREATE_SERVICE]}
            // config
            style={{ background: "var(--primary-theme-color)", color: "white", minWidth: "120px" }}
            title={"บันทึกข้อมูล"}
          />
        </Modal.Actions>
      </Modal>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  fontWeight: "normal",
  color: "#333",
  marginBottom: "4px",
};

export default ModSettingRule;
